import { OrgMemberRole } from "@humanex/interfaces";
import { Button, Input, Popup, Select } from "@humanex/ui/components";
import { useI18n } from "@humanex/ui/libs";
import { useState } from "react";

import { useSendOrgInvitation } from "@/hooks/queries";

import { RoleOptions } from "./constants";

interface OrgMemberInviteModalProps {
  orgId: string;
  isOpen: boolean;
  onClose: () => void;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export function OrgMemberInviteModal({ orgId, isOpen, onClose }: OrgMemberInviteModalProps) {
  const { t } = useI18n();

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgMemberRole>(OrgMemberRole.MEMBER);
  const [inviteError, setInviteError] = useState("");

  const { mutate: sendInvite, isPending: isInviting } = useSendOrgInvitation({
    onSuccess: () => {
      handleClose();
    },
    onError: (err) => {
      setInviteError(err instanceof Error ? err.message : t("common.error.tryAgain"));
    }
  });

  const handleClose = () => {
    setInviteEmail("");
    setInviteRole(OrgMemberRole.MEMBER);
    setInviteError("");
    onClose();
  };

  const handleInvite = () => {
    console.log(20125117, "handleInvite");
    if (isInviting) return;
    const trimmed = inviteEmail.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      setInviteError(t("org.edit.errors.invalidEmail"));
      return;
    }
    setInviteError("");
    sendInvite({ orgId, data: { email: trimmed, role: inviteRole } });
  };

  return (
    <Popup isOpen={isOpen} onClose={handleClose}>
      <div className="space-y-6">
        <div>
          <h2 className="text-responsive-lg font-semibold text-text-primary">
            {t("org.members.modal.title")}
          </h2>
          <p className="text-responsive-base text-text-secondary mt-1">
            {t("org.members.modal.description")}
          </p>
        </div>

        <div className="space-y-4">
          <Input
            type="email"
            label={t("org.members.modal.emailLabel")}
            placeholder={t("org.members.modal.emailPlaceholder")}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
            error={inviteError && !inviteEmail.trim() ? inviteError : undefined}
          />
          <Select
            label={t("org.members.modal.roleLabel")}
            options={RoleOptions}
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as OrgMemberRole)}
            required
          />
        </div>

        {inviteError && inviteEmail.trim() && (
          <div className="text-sm text-error-400 bg-error-50 p-3 rounded-sm">{inviteError}</div>
        )}

        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={handleClose} disabled={isInviting}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleInvite} disabled={isInviting || !inviteEmail.trim()}>
            {isInviting ? t("org.members.modal.inviting") : t("org.members.modal.inviteButton")}
          </Button>
        </div>
      </div>
    </Popup>
  );
}
